import { PublicKey } from "@solana/web3.js";

import type { Network } from "./constants";
import { getIndexerUrlForNetwork } from "./constants";

export type Relayer = {
  isValid: true;
  label: string;
  url: string;
  key: number;
  value: string;
  fee: unknown;
  recentActivities: unknown;
  userPubkey: unknown;
  workers: unknown;
  totalFeeEarningsSol: unknown;
  totalWithdrawCount: unknown;
  stakedAmount: unknown;
  volume24hSol: unknown;
  volume7dSol: unknown;
};

function normalizePubkeyString(value: unknown): string {
  if (value == null || value === "") {
    throw new Error("Missing public key");
  }
  if (value instanceof PublicKey) {
    return value.toBase58();
  }
  if (typeof value === "string") {
    return new PublicKey(value.trim()).toBase58();
  }
  if (
    typeof value === "object" &&
    value !== null &&
    "toBase58" in value &&
    typeof value.toBase58 === "function"
  ) {
    return value.toBase58();
  }
  return new PublicKey(value as PublicKey).toBase58();
}

/** Fetch active relayers for a cluster from the indexer. */
export async function fetchRelayersForNetwork(
  network: Network,
  indexerUrl: string = getIndexerUrlForNetwork(network)
): Promise<Relayer[]> {
  const res = await fetch(`${indexerUrl}/relayer-urls?network=${network}`);
  if (!res.ok) throw new Error("Failed to fetch relayers");

  const { data } = (await res.json()) as {
    data: Array<{
      isActive: boolean;
      url: string;
      snsDomainRaw: string;
      recentActivities: unknown;
      userPubkey: unknown;
      workers: unknown;
      totalFeeEarningsSol: unknown;
      totalWithdrawCount: unknown;
      stakedAmount: unknown;
      volume24hSol: unknown;
      volume7dSol: unknown;
    }>;
  };

  const resultRes = await Promise.allSettled(
    data
      .filter((item) => item.isActive)
      .map(async (item, index) => {
        try {
          const statusEndpoints = [
            `${item.url}/status?format=json`,
            `${item.url}/status`,
          ];
          let statusPayload: { rewardAccount: unknown; relayerFee: unknown } | null =
            null;

          for (const endpoint of statusEndpoints) {
            try {
              const response = await fetch(endpoint, {
                headers: { "Content-Type": "application/json" },
              });
              if (!response.ok) continue;
              statusPayload = await response.json();
              break;
            } catch {
              // Try the next status endpoint variant.
            }
          }

          if (!statusPayload) {
            throw new Error("Failed to fetch relayer status");
          }

          return {
            isValid: true as const,
            label: item.snsDomainRaw,
            url: item.url,
            key: index,
            value: normalizePubkeyString(statusPayload.rewardAccount),
            fee: statusPayload.relayerFee,
            recentActivities: item.recentActivities,
            userPubkey: item.userPubkey,
            workers: item.workers,
            totalFeeEarningsSol: item.totalFeeEarningsSol,
            totalWithdrawCount: item.totalWithdrawCount,
            stakedAmount: item.stakedAmount,
            volume24hSol: item.volume24hSol,
            volume7dSol: item.volume7dSol,
          };
        } catch (error) {
          const message =
            error instanceof Error ? error.message : "Unknown relayer error";
          console.warn(`Failed to fetch status for relayer ${item.url}:`, message);
          return {
            isValid: false as const,
            name: item.snsDomainRaw,
            url: item.url,
            error: message,
          };
        }
      })
  );

  return resultRes
    .filter(
      (result): result is PromiseFulfilledResult<Relayer> =>
        result.status === "fulfilled" && result.value.isValid === true
    )
    .map((result) => result.value);
}
